import { Link } from "react-router-dom";
import type { BlogGateFailureView } from "../../../../shared/blog-gate-failure";
import { useBriefDraftAttempt, useDraftBrief } from "../../hooks/useBriefDraft";

/** Walkthrough item 4: what the blog quality gate objected to, passage by passage. */
export function BlogGateFindings({ failure }: { failure: BlogGateFailureView }) {
  return (
    <div className="space-y-3">
      <p className="font-medium">{failure.headline}</p>
      {failure.findings.length > 0 && (
        <ol className="space-y-3">
          {failure.findings.map((finding, index) => (
            <li key={`${finding.dimension}-${finding.code}-${index}`} className="rounded-lg border border-amber-200 bg-white p-3 text-slate-800">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                {finding.dimension_label}{finding.code_label ? ` · ${finding.code_label}` : ""}
              </p>
              {finding.passage && <blockquote className="mt-2 border-l-2 border-amber-300 pl-3 text-sm italic text-slate-700">{finding.passage}</blockquote>}
              {finding.instruction && <p className="mt-2 text-sm"><span className="font-medium">Fix: </span>{finding.instruction}</p>}
            </li>
          ))}
        </ol>
      )}
      {failure.checks.length > 0 && (
        <ul className="list-disc space-y-1 pl-5">
          {failure.checks.map(check => <li key={check}>{check}</li>)}
        </ul>
      )}
      {failure.findings.length === 0 && failure.checks.length === 0 && (
        <p>{failure.parse_failure ? "The review model's answer could not be read, so no findings were recorded. Try again." : "No individual findings were recorded for this attempt."}</p>
      )}
    </div>
  );
}

// ideation: a brief stranded by the old Review retry; Draft this returns it to briefed (round-3 P0-2).
const RETRYABLE_STATES = new Set(["briefed", "drafting", "blocked", "ideation"]);

/**
 * The brief page's view of its latest Draft this attempt. Derived from the Run each time, so a
 * blocked attempt stays visible after a reload until a newer attempt replaces it.
 */
export function BriefDraftAttemptPanel({ brandId, campaignId, brief, onReviseBrief }: {
  brandId: string;
  campaignId: string;
  brief: { id: string; state: string };
  onReviseBrief: () => void;
}) {
  const attempt = useBriefDraftAttempt(campaignId, brief.id);
  const draft = useDraftBrief(brandId, campaignId);
  const latest = attempt.data;
  if (!latest) return null;
  const runLink = <Link to={`/runs/${encodeURIComponent(latest.run_id)}`} className="font-medium text-brand-700 underline">View run</Link>;
  if (latest.status === "running" || latest.status === "retrying" || latest.status === "queued") {
    return <section aria-label="Latest draft attempt" className="border-t border-slate-200 px-5 py-3 text-sm text-slate-700"><p role="status">Drafting in progress. {runLink}</p></section>;
  }
  if (latest.status !== "failed") return null;
  return (
    <section aria-label="Latest draft attempt" className="border-t border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">
      <h2 className="text-base font-semibold">The last draft was blocked</h2>
      <div className="mt-2">
        {latest.gate_failure
          ? <BlogGateFindings failure={latest.gate_failure} />
          : <p>{latest.error ?? latest.summary ?? "The draft attempt failed."}</p>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={onReviseBrief}
          className="min-h-11 rounded-lg border border-amber-300 bg-white px-3 font-medium text-amber-950 hover:bg-amber-100">Revise brief</button>
        {RETRYABLE_STATES.has(brief.state) && (
          <button type="button" disabled={draft.isPending || draft.isSuccess} onClick={() => draft.mutate(brief.id)}
            className="min-h-11 rounded-lg bg-brand-600 px-3 font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {draft.isPending ? "Starting…" : "Try again"}
          </button>
        )}
        {runLink}
      </div>
      {draft.isSuccess && <p role="status" className="mt-2 text-emerald-800">Drafting again. This takes a few minutes.</p>}
      {draft.isError && <p role="alert" className="mt-2">{draft.error instanceof Error ? draft.error.message : "The draft could not start."}</p>}
    </section>
  );
}
